import { use, useEffect } from "react";
import {
  SetURLSearchParams,
  ShouldRevalidateFunction,
  useLoaderData as useLoaderDataBase,
  useSearchParams,
} from "react-router-dom";
import { z } from "zod";
import {
  App,
  WebEncryptor,
  routes as allRoutes,
  fullEndpoints,
} from "@songtradr-sso/shared/common";
import {
  emailSchema,
  newPasswordSchema,
  staticText as sharedText,
  verificationCodeSchema,
} from "@songtradr-sso/shared/frontend";
import { staticText } from "../../../shared/statics/static-text";
import { Api } from "../../../shared/utils/api.utils";

// TEXT
export const text = {
  ...staticText.accountRecovery.verifyCode,
  shared: { inputs: sharedText.inputs },
};

// STYLES

// DATA
export const setNewPassword = (confirmForgotPasswordRequest: string) => {
  return new Promise<boolean>((resolve, reject) => {
    Api.post(endpoints.confirmForgotPassword, {
      confirmForgotPasswordRequest,
    })
      .then(() => {
        resolve(true);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

// TYPES
export type LoaderData = {
  decryptEmail: ReturnType<typeof decryptEmail>;
};

export type FormFields = z.infer<typeof schema>;

// CONSTANTS
export const routes = allRoutes[App.Core];
const endpoints = fullEndpoints[App.Core].auth;

export const schema = z
  .object({
    email: emailSchema,
    code: verificationCodeSchema,
    newPassword: newPasswordSchema,
    confirmPassword: z
      .string()
      .min(1, text.shared.inputs.password.confirmPassword.validation.required),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: text.shared.inputs.password.newPassword.validation.rules.match,
    path: [text.shared.inputs.password.confirmPassword.id], // Set the error on confirmPassword field
  });

// FUNCTIONS
export const useLoaderData = () => {
  const { decryptEmail } = useLoaderDataBase<LoaderData>();

  const [_, setSearchParams] = useSearchParams();
  useEffect(() => clearEmlParam(setSearchParams), [setSearchParams]);

  return { decryptEmail: use(decryptEmail) };
};

export const shouldRevalidate: ShouldRevalidateFunction = ({ nextUrl }) => {
  // Prevent revalidation when "eml" param is removed...
  return Boolean(nextUrl.searchParams.get("eml"));
};

export const decryptEmail = async (eml: string) => {
  const [cypher, passphrase] = eml.split(":");
  if (!cypher || !passphrase) return "";
  return await WebEncryptor.decrypt(cypher, passphrase);
};

const clearEmlParam = (set: SetURLSearchParams) =>
  set(
    (prev) => {
      prev.delete("eml");
      return prev;
    },
    { replace: true },
  );

// ASSETS
